import { useMutation, useSuspenseQuery } from '@apollo/client';
import { useCallback, useState } from 'react';
import GET_USER_PREFERENCES_INTERFACE from '@features/userPreferences/graphql/queries/getUserPreferencesInterface';
import UPDATE_USER_PREFERENCES_INTERFACE from '@features/userPreferences/graphql/mutation/updateUserPreferencesInterface';
import CREATE_ACTIVITY_LOGS from 'src/graphql/mutations/activityLogs/createActivityLogV2';
import SEND_FEEDBACK from 'src/graphql/mutations/sendFeedback';
import type { FeedbackSource, FeedbackType, SendFeedbackInput } from '@/__generated__/graphql';

type ReportsVersion = 'v1' | 'v2';

const REPORTS_WELCOME_DISMISSED_KEY = 'reports_welcome_dismissed';
const REPORTS_JUST_SWITCHED_TO_V1_KEY = 'reports_just_switched_to_v1';
const REPORTS_FEEDBACK_SHOWN_KEY = 'reports_feedback_shown';

export const useReportsVersion = () => {
  const [isWelcomeDismissed, setIsWelcomeDismissed] = useState(
    () => localStorage.getItem(REPORTS_WELCOME_DISMISSED_KEY) === 'true',
  );

  const { data } = useSuspenseQuery(GET_USER_PREFERENCES_INTERFACE);

  const [updatePreferences] = useMutation(UPDATE_USER_PREFERENCES_INTERFACE);
  const [createActivityLog] = useMutation(CREATE_ACTIVITY_LOGS);
  const [sendFeedbackMutation] = useMutation(SEND_FEEDBACK);

  const version: ReportsVersion =
    (data.user.preferences.interface.reportsVersion as ReportsVersion) ?? 'v2';

  const [switching, setSwitching] = useState(false);

  const switchToV1 = useCallback(async () => {
    if (switching) return;
    setSwitching(true);
    if (localStorage.getItem(REPORTS_FEEDBACK_SHOWN_KEY) !== 'true') {
      sessionStorage.setItem(REPORTS_JUST_SWITCHED_TO_V1_KEY, 'true');
      localStorage.setItem(REPORTS_FEEDBACK_SHOWN_KEY, 'true');
    }
    await updatePreferences({
      variables: { input: { reportsVersion: 'v1' } },
    });
    try {
      await createActivityLog({ variables: { activity: 'reports-switch-to-v1' } });
    } catch (err) {
      console.error('createActivityLog failed:', err);
    }
    window.location.reload();
  }, [updatePreferences, createActivityLog]);

  const switchToV2 = useCallback(async () => {
    if (switching) return;
    setSwitching(true);
    await updatePreferences({
      variables: { input: { reportsVersion: 'v2' } },
    });
    try {
      await createActivityLog({ variables: { activity: 'reports-switch-to-v2' } });
    } catch (err) {
      console.error('createActivityLog failed:', err);
    }
    window.location.reload();
  }, [updatePreferences, createActivityLog]);

  const dismissWelcome = useCallback(() => {
    localStorage.setItem(REPORTS_WELCOME_DISMISSED_KEY, 'true');
    setIsWelcomeDismissed(true);
  }, []);

  const sendFeedback = useCallback(
    async (input: Omit<SendFeedbackInput, 'source'>) => {
      try {
        await sendFeedbackMutation({
          variables: { input: { ...input, source: 'REPORTS' as FeedbackSource } },
        });
      } catch (err) {
        console.error('sendFeedback failed:', err);
      }
    },
    [sendFeedbackMutation],
  );

  const justSwitchedToV1 = sessionStorage.getItem(REPORTS_JUST_SWITCHED_TO_V1_KEY) === 'true';

  const clearJustSwitched = useCallback(() => {
    sessionStorage.removeItem(REPORTS_JUST_SWITCHED_TO_V1_KEY);
  }, []);

  return {
    version,
    switching,
    isWelcomeDismissed,
    justSwitchedToV1,
    switchToV1,
    switchToV2,
    dismissWelcome,
    sendFeedback,
    clearJustSwitched,
  };
};

export default useReportsVersion;
